import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Volume2, Send, Save, Radio, Hash, Loader2 } from "lucide-react";

const VOICES = ["alloy", "ash", "coral", "echo", "fable", "nova", "onyx", "sage", "shimmer"];
const DEFAULT_TEMPLATE = "{donor_name} just donated {amount} to {host_name}! They say: {message}";

/**
 * Admin config for Phase 3 Discord donation alerts: when a host approves a
 * donation the bot posts an @here + audio clip to a text channel AND joins the
 * "On Air" voice channel to speak a TTS announcement using this template.
 */
export default function DonationAlertSettings() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [chanErr, setChanErr] = useState("");
  const [channels, setChannels] = useState({ text: [], voice: [], bot_voice_ready: false });
  const [cfg, setCfg] = useState({
    donation_voice_alerts_enabled: false,
    donation_alert_channel_id: "",
    donation_live_vc_id: "",
    donation_tts_voice: "nova",
    donation_tts_model: "tts-1",
    donation_tts_template: DEFAULT_TEMPLATE,
  });

  const load = async () => {
    try {
      const s = await api.get("/admin/site-settings");
      const d = s.data || {};
      setCfg({
        donation_voice_alerts_enabled: !!d.donation_voice_alerts_enabled,
        donation_alert_channel_id: d.donation_alert_channel_id || "",
        donation_live_vc_id: d.donation_live_vc_id || "",
        donation_tts_voice: d.donation_tts_voice || "nova",
        donation_tts_model: d.donation_tts_model || "tts-1",
        donation_tts_template: d.donation_tts_template || DEFAULT_TEMPLATE,
      });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to load settings");
    }
    try {
      const ch = await api.get("/admin/discord/channels");
      setChannels(ch.data || { text: [], voice: [], bot_voice_ready: false });
    } catch (e) {
      setChanErr(formatApiError(e.response?.data?.detail) || "Could not load Discord channels");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const set = (k, v) => setCfg((c) => ({ ...c, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/site-settings", cfg);
      toast.success("Voice alert settings saved");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Save failed");
    } finally { setSaving(false); }
  };

  const test = async () => {
    setTesting(true);
    try {
      const { data } = await api.post("/admin/donation-alert/test");
      const r = data.report || {};
      if (r.skipped === "bot_disabled") {
        toast.error("Discord bot is disabled in Site Settings — enable it first.");
      } else {
        toast.success(
          `Test fired — TTS ${r.tts ? "✓" : "✗"} · Channel ${r.channel_post ? "✓" : "✗"} · Voice ${r.voice_play ? "✓" : "✗"}`,
          { duration: 6000 }
        );
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Test failed");
    } finally { setTesting(false); }
  };

  const selectCls = "w-full px-4 py-3 text-sm bg-[var(--ff-bg-2,#141414)] border border-[var(--ff-border,#2a2a2a)] rounded-md text-white";

  return (
    <div className="surface-card p-6" data-testid="donation-alert-settings">
      <div className="label-eyebrow text-[var(--ff-red)] mb-2 flex items-center gap-2">
        <Volume2 size={12} /> ▮ DISCORD DONATION ALERTS · VOICE + TEXT
      </div>
      <p className="text-[var(--ff-text-dim)] text-sm font-mono-ui max-w-2xl mb-5">
        When a host approves a donation, the bot posts an <b>@here</b> + the spoken audio clip to a text
        channel and joins the <b>On Air</b> voice channel to read the announcement aloud (OpenAI TTS).
      </p>

      {loading ? (
        <div className="text-[var(--ff-text-dim)] font-mono-ui text-sm">Loading…</div>
      ) : (
        <div className="space-y-5">
          {/* Master toggle */}
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={cfg.donation_voice_alerts_enabled}
              onChange={(e) => set("donation_voice_alerts_enabled", e.target.checked)}
              className="w-4 h-4 accent-[var(--ff-red)]"
              data-testid="donation-alert-enabled"
            />
            <span className="text-sm">
              Enable Discord voice + text alerts on approval
            </span>
          </label>

          {chanErr && (
            <div className="text-amber-400 text-xs font-mono-ui border border-amber-400/30 rounded-md px-3 py-2" data-testid="donation-alert-channel-error">
              {chanErr}. You can still paste IDs below manually.
            </div>
          )}

          <div className="grid md:grid-cols-2 gap-4">
            {/* Text channel */}
            <div>
              <div className="label-eyebrow mb-2 flex items-center gap-1"><Hash size={11} /> ALERT TEXT CHANNEL</div>
              {channels.text.length > 0 ? (
                <select
                  value={cfg.donation_alert_channel_id}
                  onChange={(e) => set("donation_alert_channel_id", e.target.value)}
                  className={selectCls}
                  data-testid="donation-alert-channel-select"
                >
                  <option value="">— none —</option>
                  {channels.text.map((c) => (
                    <option key={c.id} value={c.id}>#{c.name}</option>
                  ))}
                </select>
              ) : (
                <input
                  type="text"
                  value={cfg.donation_alert_channel_id}
                  onChange={(e) => set("donation_alert_channel_id", e.target.value)}
                  placeholder="Text channel ID"
                  className="w-full px-4 py-3 text-sm"
                  data-testid="donation-alert-channel-input"
                />
              )}
            </div>

            {/* Voice channel */}
            <div>
              <div className="label-eyebrow mb-2 flex items-center gap-1"><Radio size={11} /> ON AIR VOICE CHANNEL</div>
              {channels.voice.length > 0 ? (
                <select
                  value={cfg.donation_live_vc_id}
                  onChange={(e) => set("donation_live_vc_id", e.target.value)}
                  className={selectCls}
                  data-testid="donation-alert-vc-select"
                >
                  <option value="">— none —</option>
                  {channels.voice.map((c) => (
                    <option key={c.id} value={c.id}>🔊 {c.name}{c.stage ? " (stage)" : ""}</option>
                  ))}
                </select>
              ) : (
                <input
                  type="text"
                  value={cfg.donation_live_vc_id}
                  onChange={(e) => set("donation_live_vc_id", e.target.value)}
                  placeholder="Voice channel ID"
                  className="w-full px-4 py-3 text-sm"
                  data-testid="donation-alert-vc-input"
                />
              )}
            </div>

            {/* Voice */}
            <div>
              <div className="label-eyebrow mb-2">TTS VOICE</div>
              <select
                value={cfg.donation_tts_voice}
                onChange={(e) => set("donation_tts_voice", e.target.value)}
                className={selectCls}
                data-testid="donation-alert-voice-select"
              >
                {VOICES.map((v) => (
                  <option key={v} value={v}>{v}</option>
                ))}
              </select>
            </div>

            {/* Model */}
            <div>
              <div className="label-eyebrow mb-2">TTS QUALITY</div>
              <select
                value={cfg.donation_tts_model}
                onChange={(e) => set("donation_tts_model", e.target.value)}
                className={selectCls}
                data-testid="donation-alert-model-select"
              >
                <option value="tts-1">tts-1 (fast)</option>
                <option value="tts-1-hd">tts-1-hd (high quality)</option>
              </select>
            </div>
          </div>

          {/* Template */}
          <div>
            <div className="label-eyebrow mb-2">SPOKEN ANNOUNCEMENT TEMPLATE</div>
            <textarea
              value={cfg.donation_tts_template}
              maxLength={600}
              rows={2}
              onChange={(e) => set("donation_tts_template", e.target.value)}
              className="w-full px-4 py-3 text-sm font-mono-ui"
              placeholder={DEFAULT_TEMPLATE}
              data-testid="donation-alert-template"
            />
            <div className="text-[var(--ff-text-dim)] text-xs font-mono-ui mt-2">
              Placeholders: <code>{"{donor_name}"}</code> <code>{"{amount}"}</code>{" "}
              <code>{"{message}"}</code> <code>{"{host_name}"}</code> <code>{"{character}"}</code>
            </div>
          </div>

          <div className="flex flex-wrap justify-between items-center gap-3 pt-2">
            <div className="text-xs font-mono-ui text-[var(--ff-text-dim)]">
              Voice gateway:{" "}
              <span className={channels.bot_voice_ready ? "text-green-400" : "text-amber-400"} data-testid="donation-alert-voice-status">
                {channels.bot_voice_ready ? "● connected" : "○ offline"}
              </span>
            </div>
            <div className="flex gap-2">
              <button
                onClick={test}
                disabled={testing}
                className="btn-ghost text-xs border border-[var(--ff-border,#2a2a2a)]"
                data-testid="donation-alert-test-btn"
              >
                {testing ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />}
                {testing ? "Sending…" : "Send Test Alert"}
              </button>
              <button
                onClick={save}
                disabled={saving}
                className="btn-primary text-xs"
                data-testid="donation-alert-save-btn"
              >
                <Save size={13} /> {saving ? "Saving…" : "Save Settings"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
